import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Users, Briefcase, ArrowLeft } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import type { VagaRH } from '../types/rh';

const VagasPage = () => {
  const [vagas, setVagas] = useState<VagaRH[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroLocalidade, setFiltroLocalidade] = useState('');

  useEffect(() => {
    carregarVagas();
  }, []);

  const carregarVagas = async () => {
    try {
      const { data, error } = await supabase
        .from('vagas_rh')
        .select('*')
        .eq('status', 'ativa')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVagas(data || []);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    } finally {
      setLoading(false);
    }
  };

  const vagasFiltradas = vagas.filter(vaga => {
    const tipoMatch = !filtroTipo || vaga.tipo_vaga.toLowerCase().includes(filtroTipo.toLowerCase());
    const localidadeMatch = !filtroLocalidade || vaga.localidade.toLowerCase().includes(filtroLocalidade.toLowerCase());
    return tipoMatch && localidadeMatch;
  });

  const getLocalidadeBadgeColor = (tipo: string) => {
    switch (tipo) {
      case 'remoto': return 'bg-green-100 text-green-800';
      case 'hibrido': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando vagas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header com Logo */}
      <header className="absolute top-0 left-0 right-0 z-10 p-6">
        <div className="flex items-center">
          <img 
            src="/lovable-uploads/1f6dcb92-5dfc-4a75-aa2e-c3251eb28c93.png" 
            alt="Fox Vendas Logo" 
            className="h-12 w-auto"
          />
        </div>
      </header>

      {/* Header Principal */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 py-16 pt-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-6">
            <Link to="/trabalhe-conosco" className="text-white hover:text-gray-200 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </Link>
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Vagas Disponíveis
            </h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Encontre a oportunidade perfeita para sua carreira na Fox Vendas
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtrar Vagas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Vaga
              </label>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                <option value="Corretor">Corretor</option>
                <option value="Coordenador">Coordenador</option>
                <option value="Gerente">Gerente</option>
                <option value="Diretor">Diretor</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localidade
              </label>
              <select
                value={filtroLocalidade}
                onChange={(e) => setFiltroLocalidade(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Todas as localidades</option>
                <option value="Campinas">Campinas</option>
                <option value="Paulínia">Paulínia</option>
                <option value="Remoto">Remoto</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Vagas */}
        <div className="grid grid-cols-1 gap-6">
          {vagasFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma vaga encontrada
              </h3>
              <p className="text-gray-600">
                Tente ajustar os filtros ou volte mais tarde para novas oportunidades.
              </p>
            </div>
          ) : (
            vagasFiltradas.map((vaga) => (
              <div key={vaga.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {vaga.titulo}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {vaga.localidade}
                          </div>
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {vaga.tipo_vaga}
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {new Date(vaga.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getLocalidadeBadgeColor(vaga.tipo_localidade)}`}>
                          {vaga.tipo_localidade}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {vaga.descricao.substring(0, 200)}...
                    </p>
                  </div>
                  
                  <div className="flex space-x-3 mt-4 lg:mt-0 lg:ml-6">
                    <Link to={`/vaga/${vaga.id}`}>
                      <button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                        Ver Detalhes
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* CTA Footer */}
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg p-8 mt-12 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Não encontrou a vaga ideal?
          </h3>
          <p className="text-gray-200 mb-6">
            Deixe seu currículo em nosso banco de talentos e seja notificado sobre novas oportunidades.
          </p>
          <button 
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full transition-colors"
            onClick={() => {
              const element = document.getElementById('chat-button');
              if (element) element.click();
            }}
          >
            Falar com Recrutador
          </button>
        </div>
      </div>
    </div>
  );
};

export default VagasPage;